import React from 'react'
import dayjs from 'dayjs'
import clsx from 'clsx'
import { BlockWrapper, defineBlock } from '@smartfire/js-block-engine'
import { Score } from '../../score/block'
import { useCompetitionProps } from '../../hooks/useCompetitionProps'
import { BooleanBitString, Poule, Rencontre, getJournees } from '../types'
import { CompetitionSearchNoResult } from '../../components/CompetitionSearchNoResult'
import { competitionRencontreGenerateUrl, toStructureLogoSrc } from '../../shared/competitionUrls'
import { booleanBitStringToBoolean } from '../../shared/utils'
import { ReactComponent as Rematch } from '../../assets/pictoRematch.svg'
import styles from './styles.module.scss'

interface Attributes {
    poule: Poule
    selected_numero_journee: string
    rencontres: (Rencontre & {
        phaseLibelle: string
        structure1Logo: string
        structure2Logo: string
        equipe1ShowLogo: BooleanBitString
        equipe2ShowLogo: BooleanBitString
        equipe1Libelle: string
        equipe2Libelle: string
        extPouleId: string
        rematchDispo: BooleanBitString
    })[]
}

const RencontreList = (attributes) => {
    const {
        attributes: { poule, selected_numero_journee, rencontres },
        params: { ext_saison_id, url_competition_type, url_competition },
    } = useCompetitionProps<Attributes>({
        initDataFromPhpFirstRendering: attributes,
    })

    const journees = getJournees(poule)
    const journee = journees.find((x) => String(x.journee_numero) === selected_numero_journee)

    return (
        <div>
            {selected_numero_journee && journee ? (
                <div className={styles.header}>
                    {dayjs(journee.date_debut).isValid() && dayjs(journee.date_fin).isValid() ? (
                        <div className={styles.title}>
                            {dayjs(journee.date_debut).format('LL')} au{' '}
                            {dayjs(journee.date_fin).format('LL')}
                        </div>
                    ) : null}
                    <div className={styles.subtitle}>
                        Date de mise à jour :{' '}
                        <b>{dayjs(poule.dateDernierUpdateEnfants).format('ll à HH[h]mm')}</b>
                    </div>
                </div>
            ) : null}

            {rencontres.length ? (
                rencontres.map((r) => (
                    <a
                        key={r.id}
                        className={styles.rencontre}
                        href={competitionRencontreGenerateUrl({
                            ext_saison_id,
                            url_competition_type,
                            url_competition,
                            ext_poule_id: r.extPouleId,
                            ext_rencontre_id: r.ext_rencontreId,
                        })}
                    >
                        <Score
                            home={{
                                name: r.equipe1Libelle,
                                flag: {
                                    url: booleanBitStringToBoolean(r.equipe1ShowLogo)
                                        ? toStructureLogoSrc(r.structure1Logo, 64)
                                        : undefined,
                                },
                                score: r.equipe1Score,
                            }}
                            away={{
                                name: r.equipe2Libelle,
                                flag: {
                                    url: booleanBitStringToBoolean(r.equipe2ShowLogo)
                                        ? toStructureLogoSrc(r.structure2Logo, 64)
                                        : undefined,
                                },
                                score: r.equipe2Score,
                            }}
                            date={r.date}
                            title={
                                selected_numero_journee
                                    ? ''
                                    : journees.length <= 1 && r.phaseLibelle
                                    ? r.phaseLibelle
                                    : `Journée ${r.journeeNumero}`
                            }
                        />
                        <div className={styles.cta}>
                            <i className={clsx('fi fi-rs-angle-small-right', styles.ctaIcon)} />
                            <span className={styles.ctaText}>Voir détail du match</span>
                            <br />
                            {booleanBitStringToBoolean(r.rematchDispo) && (
                                <span className={styles.rematch}>
                                    <Rematch /> Rematch disponible
                                </span>
                            )}
                        </div>
                    </a>
                ))
            ) : (
                <CompetitionSearchNoResult />
            )}
        </div>
    )
}

const RencontreListBlock = (attributes) => (
    <BlockWrapper.Without>
        <RencontreList {...attributes} />
    </BlockWrapper.Without>
)

export default defineBlock({
    Block: RencontreListBlock,
})
